import { useCallback, useEffect, useRef, useState } from 'react';
import { DRAFT_CONFLICT_CODE, type DraftConflictDetails, type MyScorecardView, type ScorecardDraft } from '@nest-recruit/shared';
import { ApiError } from '../../../lib/api-client';
import { useNetworkStatus } from '../../../shell/SocketProvider';
import { scorecardsApi } from '../api/scorecards-api';
import { readStored, reconcile, sameDraft, storageKey, writeStored, type ServerCopy } from './draft-store';

/** Typing pauses this long before the server is told; the device is told at once. */
const SAVE_AFTER_MS = 1_000;
/** When the server could not be reached, how soon to try again even without an "online" signal. */
const RETRY_MS = 5_000;

export type SaveStatus = 'saved' | 'saving' | 'waiting' | 'error' | 'conflict';

export interface ScorecardDraftState {
  readonly draft: ScorecardDraft;
  readonly baseRevision: number;
  readonly status: SaveStatus;
  readonly savedAt: string | null;
  /** The server's copy when both it and this device changed (F11.06). */
  readonly conflict: ServerCopy | null;
  readonly isDirty: boolean;
  /** The device refused to store (full, or private browsing) — the screen must say so. */
  readonly isDeviceStorageFailing: boolean;
  readonly errorMessage: string | null;
  change(next: ScorecardDraft): void;
  /** "Keep this device's version" — local wins, overwriting the other copy. */
  keepMine(): void;
  useTheirs(): void;
  /** After a submit the server holds exactly what was sent. */
  settle(scorecard: MyScorecardView): void;
  /**
   * Sends anything waiting and resolves with what the server now holds — what Submit
   * sends, so it never races a save still on its way. Null when it could not (offline,
   * or a conflict to answer first).
   */
  saveNow(): Promise<{ readonly draft: ScorecardDraft; readonly baseRevision: number } | null>;
}

/**
 * F11.05 / F11.06 — the cockpit's draft. Every change goes to this device first (so wifi
 * dropping in a meeting room loses nothing), then to the server a second after typing
 * stops, or the moment the connection comes back. A save from an older revision than the
 * server's comes back as a conflict, and the panelist chooses — this device by default.
 */
export function useScorecardDraft(scorecard: MyScorecardView, userId: string): ScorecardDraftState {
  const key = storageKey(userId, scorecard.id);
  const network = useNetworkStatus();
  const [initial] = useState(() => start(key, scorecard));
  const [draft, setDraft] = useState<ScorecardDraft>(initial.draft);
  const [baseRevision, setBaseRevision] = useState(initial.baseRevision);
  const [isDirty, setIsDirty] = useState(initial.isDirty);
  const [status, setStatus] = useState<SaveStatus>(initial.conflict ? 'conflict' : initial.isDirty ? 'waiting' : 'saved');
  const [savedAt, setSavedAt] = useState<string | null>(scorecard.draftSavedAt);
  const [conflict, setConflict] = useState<ServerCopy | null>(initial.conflict);
  const [isDeviceStorageFailing, setIsDeviceStorageFailing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // What the save loop reads — refs, so a save in flight sees the latest keystroke.
  const live = useRef({ draft: initial.draft, baseRevision: initial.baseRevision, isDirty: initial.isDirty, wasOffline: initial.wasOffline, isOverwrite: false, isInFlight: false, isAgainNeeded: false, hasConflict: initial.conflict !== null });
  const timer = useRef<number | null>(null);
  const flushRef = useRef<() => Promise<void>>(async () => undefined);
  const inFlight = useRef<Promise<void> | null>(null);

  const persist = useCallback(
    (next: { readonly draft: ScorecardDraft; readonly baseRevision: number; readonly isDirty: boolean; readonly wasOffline: boolean }): void => {
      const isStored = writeStored(window.localStorage, key, { ...next, updatedAt: new Date().toISOString() });
      setIsDeviceStorageFailing(!isStored);
    },
    [key],
  );

  const schedule = useCallback((delay: number): void => {
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => void flushRef.current(), delay);
  }, []);

  const send = useCallback(async (): Promise<void> => {
    const state = live.current;
    if (!state.isDirty || state.hasConflict) return;
    if (state.isInFlight) {
      state.isAgainNeeded = true;
      return;
    }
    if (!navigator.onLine) {
      state.wasOffline = true;
      persist(state);
      setStatus('waiting');
      return;
    }
    const sent = state.draft;
    state.isInFlight = true;
    setStatus('saving');
    try {
      const result = await scorecardsApi.saveDraft(scorecard.id, { baseRevision: state.baseRevision, draft: sent, isOfflineSync: state.wasOffline, isOverwrite: state.isOverwrite });
      state.baseRevision = result.draftRevision;
      state.wasOffline = false;
      state.isOverwrite = false;
      state.isDirty = !sameDraft(state.draft, sent);
      persist(state);
      setBaseRevision(result.draftRevision);
      setSavedAt(result.draftSavedAt);
      setIsDirty(state.isDirty);
      setErrorMessage(null);
      setStatus(state.isDirty ? 'saving' : 'saved');
      if (state.isDirty) schedule(0);
    } catch (error) {
      if (error instanceof ApiError && error.code === DRAFT_CONFLICT_CODE && isConflictDetails(error.details)) {
        const server = { draft: error.details.serverDraft, revision: error.details.serverRevision, savedAt: error.details.savedAt };
        state.hasConflict = true;
        setConflict(server);
        setStatus('conflict');
      } else if (error instanceof ApiError && error.status === 0) {
        // The wifi dropped mid-save: nothing is lost, it goes again when the line is back.
        state.wasOffline = true;
        persist(state);
        setStatus('waiting');
        schedule(RETRY_MS);
      } else {
        setErrorMessage(error instanceof Error ? error.message : null);
        setStatus('error');
      }
    } finally {
      state.isInFlight = false;
      if (state.isAgainNeeded) {
        state.isAgainNeeded = false;
        schedule(0);
      }
    }
  }, [persist, schedule, scorecard.id]);

  const flush = useCallback(async (): Promise<void> => {
    const running = send();
    inFlight.current = running;
    await running;
  }, [send]);
  useEffect(() => {
    flushRef.current = flush;
  }, [flush]);

  const saveNow = useCallback(async (): Promise<{ readonly draft: ScorecardDraft; readonly baseRevision: number } | null> => {
    if (timer.current !== null) window.clearTimeout(timer.current);
    // Let a save already on its way land, then send whatever came after it.
    if (inFlight.current) await inFlight.current;
    await flush();
    if (inFlight.current) await inFlight.current;
    const state = live.current;
    if (state.hasConflict || state.isDirty) return null;
    return { draft: state.draft, baseRevision: state.baseRevision };
  }, [flush]);

  const change = useCallback(
    (next: ScorecardDraft): void => {
      const state = live.current;
      state.draft = next;
      state.isDirty = true;
      if (!navigator.onLine) state.wasOffline = true;
      // The device first, on every keystroke — this line is the whole of "nothing is lost".
      persist(state);
      setDraft(next);
      setIsDirty(true);
      if (!state.hasConflict) {
        setStatus(navigator.onLine ? 'saving' : 'waiting');
        schedule(SAVE_AFTER_MS);
      }
    },
    [persist, schedule],
  );

  const keepMine = useCallback((): void => {
    const state = live.current;
    if (!conflict) return;
    state.baseRevision = conflict.revision;
    state.isOverwrite = true;
    state.isDirty = true;
    state.hasConflict = false;
    persist(state);
    setConflict(null);
    setBaseRevision(conflict.revision);
    void flush();
  }, [conflict, flush, persist]);

  const useTheirs = useCallback((): void => {
    const state = live.current;
    if (!conflict) return;
    Object.assign(state, { draft: conflict.draft, baseRevision: conflict.revision, isDirty: false, wasOffline: false, isOverwrite: false, hasConflict: false });
    persist(state);
    setDraft(conflict.draft);
    setBaseRevision(conflict.revision);
    setIsDirty(false);
    setSavedAt(conflict.savedAt);
    setConflict(null);
    setStatus('saved');
  }, [conflict, persist]);

  const settle = useCallback(
    (next: MyScorecardView): void => {
      const state = live.current;
      Object.assign(state, { draft: next.draft, baseRevision: next.draftRevision, isDirty: false, wasOffline: false, isOverwrite: false, hasConflict: false });
      persist(state);
      setDraft(next.draft);
      setBaseRevision(next.draftRevision);
      setIsDirty(false);
      setSavedAt(next.draftSavedAt);
      setConflict(null);
      setStatus('saved');
    },
    [persist],
  );

  // A newer copy on the server (another device, an HR Lead's correction) arrives with a
  // refetch. Ignored while this device is mid-save, when it is our own save coming back.
  useEffect(() => {
    const state = live.current;
    if (state.isInFlight || scorecard.draftRevision === state.baseRevision) return;
    const result = reconcile({ draft: state.draft, baseRevision: state.baseRevision, isDirty: state.isDirty, wasOffline: state.wasOffline, updatedAt: '' }, serverCopy(scorecard));
    if (result.kind === 'local') return;
    if (result.kind === 'server') {
      Object.assign(state, { draft: result.draft, baseRevision: result.baseRevision, isDirty: false });
      persist(state);
      setDraft(result.draft);
      setBaseRevision(result.baseRevision);
      setIsDirty(false);
      setSavedAt(scorecard.draftSavedAt);
      setStatus('saved');
      return;
    }
    state.hasConflict = true;
    setConflict(result.server);
    setStatus('conflict');
  }, [scorecard, persist]);

  // F11.06: the line is back — send what waited.
  useEffect(() => {
    if (network === 'online' && live.current.isDirty) void flush();
    if (network === 'offline' && live.current.isDirty) setStatus((current) => (current === 'conflict' ? current : 'waiting'));
  }, [network, flush]);

  // A draft left dirty from a previous visit (closed tab, crash) goes as soon as the screen opens.
  useEffect(() => {
    if (live.current.isDirty) schedule(0);
    return () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    };
  }, [schedule]);

  return { draft, baseRevision, status, savedAt, conflict, isDirty, isDeviceStorageFailing, errorMessage, change, keepMine, useTheirs, settle, saveNow };
}

function serverCopy(scorecard: MyScorecardView): ServerCopy {
  return { draft: scorecard.draft, revision: scorecard.draftRevision, savedAt: scorecard.draftSavedAt };
}

/** Where the cockpit starts: the device's copy or the server's, per `reconcile`. */
function start(key: string, scorecard: MyScorecardView): { draft: ScorecardDraft; baseRevision: number; isDirty: boolean; wasOffline: boolean; conflict: ServerCopy | null } {
  const result = reconcile(readStored(window.localStorage, key), serverCopy(scorecard));
  if (result.kind === 'server') return { draft: result.draft, baseRevision: result.baseRevision, isDirty: false, wasOffline: false, conflict: null };
  if (result.kind === 'local') return { draft: result.draft, baseRevision: result.baseRevision, isDirty: true, wasOffline: result.wasOffline, conflict: null };
  return { draft: result.draft, baseRevision: scorecard.draftRevision, isDirty: true, wasOffline: result.wasOffline, conflict: result.server };
}

function isConflictDetails(value: unknown): value is DraftConflictDetails {
  if (typeof value !== 'object' || value === null) return false;
  return typeof Reflect.get(value, 'serverRevision') === 'number' && typeof Reflect.get(value, 'serverDraft') === 'object';
}
